import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { adaptEchartsOptions, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { LineChart } from 'echarts/charts';
import { GraphicComponent, GridComponent, MarkPointComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([LineChart, SVGRenderer, GraphicComponent, GridComponent, MarkPointComponent]);

@Component({
  selector: 'dojo-pdf-explorer',
  standalone: true,
  imports: [NgxEchartsDirective],
  template: `
    <div
      echarts
      class="pdf-explorer-chart"
      [options]="chartOptions()"
      [initOpts]="initOpts"
      [autoResize]="true"
      (chartInit)="onChartInit($event)"
    ></div>
    <div class="pdf-explorer-result">
      <span class="pdf-explorer-p">P</span>(<span class="pdf-explorer-bound">{{ aDisplay() }}</span> ≤
      <span class="pdf-explorer-x">X</span> ≤ <span class="pdf-explorer-bound">{{ bDisplay() }}</span
      >)
      <span class="pdf-explorer-eq">=</span>
      <span class="pdf-explorer-val">{{ probabilityDisplay() }}</span>
    </div>
  `,
  styleUrl: './pdf-explorer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [provideEchartsCore({ echarts })],
})
export class PdfExplorerComponent {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private chart: any = null;

  // ── Inputs ──
  readonly curveData = input<number[][]>([]);
  readonly zeroData = input<number[][]>([]);
  readonly pdfCoeffs = input<number[]>([]);
  readonly domainMin = input(0);
  readonly domainMax = input(3);
  readonly initialA = input(1);
  readonly initialB = input(2.5);
  readonly xMin = input(0);
  readonly xMax = input(3.4);
  readonly yMax = input(0.55);
  readonly xInterval = input(1);
  readonly curveColor = input('#3b82f6');
  readonly boundsColor = input('#f97316');
  readonly areaColor = input('rgba(59, 130, 246, 0.12)');
  readonly shadedColor = input('rgba(249, 115, 22, 0.25)');
  readonly curveLabel = input('');
  readonly curveLabelCoord = input<number[]>([]);
  readonly smooth = input(true);

  // ── State ──
  readonly aVal = signal(1);
  readonly bVal = signal(2.5);

  // ── Computed ──
  readonly aDisplay = computed(() => {
    const v = this.aVal();
    return Number.isInteger(v) ? v.toString() : v.toFixed(1);
  });

  readonly bDisplay = computed(() => {
    const v = this.bVal();
    return Number.isInteger(v) ? v.toString() : v.toFixed(1);
  });

  readonly probability = computed(() => {
    const coeffs = this.pdfCoeffs();
    if (coeffs.length === 0) {
      return 0;
    }
    return this.evalAntideriv(this.bVal(), coeffs) - this.evalAntideriv(this.aVal(), coeffs);
  });

  readonly probabilityDisplay = computed(() => {
    const p = Math.round(this.probability() * 1000) / 1000;
    if (Number.isInteger(p)) {
      return p.toString();
    }
    // Show up to 3 decimal places, trimming trailing zeros
    return p.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  });

  readonly initOpts = { renderer: 'svg' } as const;

  readonly chartOptions = computed((): EChartsOption => {
    const data = this.curveData();
    const color = this.curveColor();
    const label = this.curveLabel();
    const labelCoord = this.curveLabelCoord();
    const zero = this.zeroData();

    const options: EChartsOption = {
      animation: false,
      grid: { left: 40, right: 15, top: 25, bottom: 30 },
      xAxis: {
        type: 'value',
        min: this.xMin(),
        max: this.xMax(),
        interval: this.xInterval(),
        axisLabel: { fontSize: 11, color: '#6b7280' },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisTick: { lineStyle: { color: '#d1d5db' } },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: this.yMax(),
        interval: 0.1,
        axisLabel: { fontSize: 11, color: '#6b7280' },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisTick: { lineStyle: { color: '#d1d5db' } },
      },
      series: [
        {
          type: 'line',
          smooth: this.smooth(),
          symbol: 'none',
          lineStyle: { color, width: 2.5 },
          areaStyle: { color: this.areaColor() },
          data,
          ...(label && labelCoord.length >= 2
            ? {
                markPoint: {
                  symbol: 'circle',
                  symbolSize: 0,
                  data: [
                    {
                      name: 'curve-label',
                      coord: labelCoord,
                      label: { show: true, formatter: label, fontSize: 12, fontWeight: 'bold', color },
                    },
                  ],
                },
              }
            : {}),
        },
        {
          type: 'line',
          smooth: this.smooth(),
          symbol: 'none',
          lineStyle: { width: 0 },
          areaStyle: { color: this.shadedColor() },
          data: this.getShadedData(data, this.initialA(), this.initialB()),
          silent: true,
        },
        ...(zero.length
          ? [
              {
                type: 'line' as const,
                smooth: false,
                symbol: 'none' as const,
                lineStyle: { color, width: 2.5 },
                areaStyle: { color: this.areaColor() },
                data: zero,
                silent: true,
              },
            ]
          : []),
      ],
    };

    return adaptEchartsOptions(options, this.document, this.isDark());
  });

  // ── Lifecycle ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChartInit(chart: any): void {
    this.chart = chart;
    this.aVal.set(this.initialA());
    this.bVal.set(this.initialB());

    // Wait for chart layout, then add drag handles
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.addDragHandles());
    });

    // Reposition on window resize
    const onResize = () => {
      requestAnimationFrame(() => this.addDragHandles());
    };
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
  }

  // ── Private ──
  private addDragHandles(): void {
    const chart = this.chart;
    if (!chart) {
      return;
    }

    const gridRect = this.getGridRect();
    if (!gridRect) {
      return;
    }

    const color = this.boundsColor();
    const toPixelX = (v: number): number => chart.convertToPixel('grid', [v, 0])?.[0] ?? 0;
    const toDataX = (px: number): number => chart.convertFromPixel('grid', [px, 0])?.[0] ?? 0;
    const snap = (v: number): number => Math.round(v * 20) / 20;
    const SNAP_THRESHOLD = 0.12;

    const makeHandle = (id: string, dataX: number, label: string, clamp: (rawDataX: number) => number) => ({
      id,
      type: 'group' as const,
      draggable: 'horizontal' as const,
      position: [toPixelX(dataX), 0],
      z: 100,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ondrag: (e: any) => {
        const raw = toDataX(e.target.position[0]);
        const clamped = clamp(raw);
        // Force the graphic element to the clamped pixel position
        e.target.position[0] = toPixelX(clamped);
      },
      children: [
        {
          type: 'line' as const,
          shape: { x1: 0, y1: gridRect.y, x2: 0, y2: gridRect.y + gridRect.height },
          style: { stroke: color, lineWidth: 2, lineDash: [6, 4] },
        },
        {
          type: 'rect' as const,
          shape: { x: -12, y: gridRect.y, width: 24, height: gridRect.height },
          style: { fill: 'rgba(0,0,0,0)' },
          cursor: 'ew-resize' as const,
        },
        {
          type: 'text' as const,
          position: [0, gridRect.y + gridRect.height + 4],
          style: {
            text: label,
            fill: color,
            fontSize: 10,
            fontWeight: 'bold' as const,
            textAlign: 'center' as const,
            backgroundColor: '#fff',
            padding: [2, 4],
            borderRadius: 3,
          },
        },
      ],
    });

    chart.setOption({
      graphic: [
        makeHandle('handle-a', this.aVal(), 'a', (raw: number) => {
          let v = snap(Math.max(this.domainMin(), Math.min(raw, this.bVal() - 0.05)));
          // Snap to domain boundary when close
          if (Math.abs(v - this.domainMin()) < SNAP_THRESHOLD) {
            v = this.domainMin();
          }
          this.aVal.set(v);
          this.updateShadedSeries();
          return v;
        }),
        makeHandle('handle-b', this.bVal(), 'b', (raw: number) => {
          let v = snap(Math.min(this.domainMax(), Math.max(raw, this.aVal() + 0.05)));
          // Snap to domain boundary when close
          if (Math.abs(v - this.domainMax()) < SNAP_THRESHOLD) {
            v = this.domainMax();
          }
          this.bVal.set(v);
          this.updateShadedSeries();
          return v;
        }),
      ],
    });
  }

  private updateShadedSeries(): void {
    const chart = this.chart;
    if (!chart) {
      return;
    }

    const shadedData = this.getShadedData(this.curveData(), this.aVal(), this.bVal());
    chart.setOption({
      series: [{}, { data: shadedData }],
    });
  }

  private getGridRect(): { x: number; y: number; width: number; height: number } | null {
    try {
      return this.chart?.getModel()?.getComponent('grid', 0)?.coordinateSystem?.getRect() ?? null;
    } catch {
      return null;
    }
  }

  private getShadedData(allData: number[][], a: number, b: number): number[][] {
    const result: number[][] = [];

    for (let i = 0; i < allData.length; i++) {
      const [x, y] = allData[i];

      // Interpolate entry at a
      if (i > 0) {
        const [px, py] = allData[i - 1];
        if (px < a && x >= a) {
          const t = (a - px) / (x - px);
          result.push([a, py + t * (y - py)]);
        }
      }

      if (x >= a && x <= b) {
        result.push([x, y]);
      }

      // Interpolate exit at b
      if (i < allData.length - 1) {
        const [nx, ny] = allData[i + 1];
        if (x <= b && nx > b) {
          const t = (b - x) / (nx - x);
          result.push([b, y + t * (ny - y)]);
        }
      }
    }

    return result;
  }

  private evalAntideriv(x: number, coeffs: number[]): number {
    let result = 0;
    for (let i = 0; i < coeffs.length; i++) {
      result += (coeffs[i] / (i + 1)) * Math.pow(x, i + 1);
    }
    return result;
  }
}
